<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onReceiptSearch"
          v-model="filter_search"
          class="input--300"
          type="search"
          placeholder="Search by verification code"
          name="search"
        />
        <VueCtkDateTimePicker
          id="date_picker_leads_request"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="fetchAll"
          @input="onInputDatePicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Date range"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker"
        />
        <app-select
          @search="onSupplierSearch"
          @input="onChangeFilter"
          v-model="filter_supplier"
          :options="supplier_options"
          :filterBy="() => true"
          :display-field="option => `${option.trade_name === option.legal_name ? option.trade_name : `${option.trade_name} (${option.legal_name})`}`"
          label="trade_name"
          placeholder="Supplier"
          class="select--300"
        />
      </app-cells>
      <app-colored-notes
        color="#35a4ea"
        text="This page shows all records imported from the monthly VAT Return. Everything is gathered here in one place and grouped into sections based on detected issues below."
      />
      <template v-if="table_supplier.items.length">
        <app-cells><h2 class="title">Missing Supplier</h2></app-cells>
        <app-colored-notes color="#35a4ea">This section lists records from suppliers that are not yet in our database. The Supplier column shows the legal name of the supplier, followed by their VRN number in brackets.<br>To fix this, create the missing supplier in the system, then go to the related VAT Statement and click <b>“Link Suppliers & EFD Receipts.”</b>
        </app-colored-notes>
        <v-client-table
          :data="table_supplier.items"
          :columns="table_supplier.columns"
          :options="table_supplier.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_supplier }"
        >
          <template #h__statement><div class="table-default__left">Statement</div></template>
          <template #h__verification_code><div class="table-default__left">Verification code</div></template>
          <template #h__supplier><div class="table-default__left">Supplier</div></template>
          <template #statement="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'finance-taxes-vat-statement-detail', params: { id: row.vat_statement.id } }"
                class="table-default__link-border table-default__link-border--inline"
              >
                {{ dateMonthFormat(row.vat_statement.from_date) }}
              </router-link>
            </div>
          </template>
          <template #verification_code="{ row }"><div class="table-default__left">{{ row.verification_code }}</div></template>
          <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
          <template #amount_total="{ row }">{{ formatAmount(row.amount_total) }}</template>
          <template #amount_excluded_vat="{ row }">{{ formatAmount(row.amount_excluded_vat) }}</template>
          <template #amount_vat="{ row }">{{ formatAmount(row.amount_vat) }}</template>
          <template #supplier="{ row }"><div class="table-default__left">{{ row.missing_supplier || '-' }}</div></template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPagination"
              v-if="pagination_supplier.pages > 1"
              v-model="pagination_supplier.page"
              :page-count="pagination_supplier.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_supplier.count" :count="table_supplier.items.length" :page="pagination_supplier.page" />
        </app-cells>
      </template>
      <template v-if="table_efd.items.length">
        <app-cells><h2 class="title">Missing EFD</h2></app-cells>
        <v-client-table
          :data="table_efd.items"
          :columns="table_efd.columns"
          :options="table_efd.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_efd }"
        >
          <template #h__statement><div class="table-default__left">Statement</div></template>
          <template #h__verification_code><div class="table-default__left">Verification code</div></template>
          <template #h__supplier><div class="table-default__left">Supplier</div></template>
          <template #statement="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'finance-taxes-vat-statement-detail', params: { id: row.vat_statement.id } }"
                class="table-default__link-border table-default__link-border--inline"
              >
                {{ dateMonthFormat(row.vat_statement.from_date) }}
              </router-link>
            </div>
          </template>
          <template #verification_code="{ row }"><div class="table-default__left">{{ row.verification_code }}</div></template>
          <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
          <template #amount_total="{ row }">{{ formatAmount(row.amount_total) }}</template>
          <template #amount_excluded_vat="{ row }">{{ formatAmount(row.amount_excluded_vat) }}</template>
          <template #amount_vat="{ row }">{{ formatAmount(row.amount_vat) }}</template>
          <template #supplier="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
                class="table-default__link-inline"
              >
                {{ row.supplier.legal_name }}
              </router-link>
            </div>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationEFD"
              v-if="pagination_efd.pages > 1"
              v-model="pagination_efd.page"
              :page-count="pagination_efd.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_efd.count" :count="table_efd.items.length" :page="pagination_efd.page" />
        </app-cells>
      </template>
      <template v-if="table_purchases.items.length">
        <app-cells><h2 class="title">Missing Purchases</h2></app-cells>
        <app-colored-notes color="#35a4ea">This section lists EFD receipts that were manually added to our system but have no matching record in the TRA VAT Statement.<br>To fix this, verify the receipt details directly in the TRA portal to ensure the EFD record is valid and correctly reported.
        </app-colored-notes>
        <v-client-table
          :data="table_purchases.items"
          :columns="table_purchases.columns"
          :options="table_purchases.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_purchases }"
        >
          <template #h__verification_code><div class="table-default__left">Verification code</div></template>
          <template #h__supplier><div class="table-default__left">Supplier</div></template>
          <template #verification_code="{ row }">
            <div class="table-default__left">
              <button
                @click="onCopyToClipboard(row.verification_link)"
                class="table-default__copy"
                :class="{'table-default__copy--disabled': !row.verification_link}"
              >
                {{ row.verification_code }}
              </button>
            </div>
          </template>
          <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
          <template #amount_total="{ row }">{{ formatAmount(row.amount_total) }}</template>
          <template #amount_excluded_vat="{ row }">{{ formatAmount(row.amount_vat_exc) }}</template>
          <template #amount_vat="{ row }">{{ formatAmount(row.amount_vat) }}</template>
          <template #supplier="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
                class="table-default__link-inline"
              >
                {{ row.supplier.legal_name }}
              </router-link>
            </div>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationPurchases"
              v-if="pagination_purchases.pages > 1"
              v-model="pagination_purchases.page"
              :page-count="pagination_purchases.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_purchases.count" :count="table_purchases.items.length" :page="pagination_purchases.page" />
        </app-cells>
      </template>
      <template v-if="table_amount.items.length">
        <app-cells><h2 class="title">Amount Not Match</h2></app-cells>
        <app-colored-notes color="#35a4ea">This section lists EFD receipts where the total amount does not match the data received from the TRA.<br>To fix this, review the receipt amount and update it so that it matches the official TRA record.
        </app-colored-notes>
        <v-client-table
          :data="table_amount.items"
          :columns="table_amount.columns"
          :options="table_amount.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_amount }"
        >
          <template #h__statement><div class="table-default__left">Statement</div></template>
          <template #h__verification_code><div class="table-default__left">Verification code</div></template>
          <template #h__supplier><div class="table-default__left">Supplier</div></template>
          <template #statement="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'finance-taxes-vat-statement-detail', params: { id: row.vat_statement.id } }"
                class="table-default__link-border table-default__link-border--inline"
              >
                {{ dateMonthFormat(row.vat_statement.from_date) }}
              </router-link>
            </div>
          </template>
          <template #verification_code="{ row }"><div class="table-default__left">{{ row.verification_code }}</div></template>
          <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
          <template #amount_total="{ row }">{{ formatAmount(row.amount_total) }}</template>
          <template #amount_excluded_vat="{ row }">{{ formatAmount(row.amount_excluded_vat) }}</template>
          <template #amount_vat="{ row }">{{ formatAmount(row.amount_vat) }}</template>
          <template #supplier="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
                class="table-default__link-inline"
              >
                {{ row.supplier.legal_name }}
              </router-link>
            </div>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationAmount"
              v-if="pagination_amount.pages > 1"
              v-model="pagination_amount.page"
              :page-count="pagination_amount.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_amount.count" :count="table_amount.items.length" :page="pagination_amount.page" />
        </app-cells>
      </template>
      <app-cells><h2 class="title">Records</h2></app-cells>
      <v-client-table
        :data="table_records.items"
        :columns="table_records.columns"
        :options="table_records.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader_records }"
      >
        <template #h__statement><div class="table-default__left">Statement</div></template>
        <template #h__verification_code><div class="table-default__left">Verification code</div></template>
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #statement="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'finance-taxes-vat-statement-detail', params: { id: row.vat_statement.id } }"
              class="table-default__link-border table-default__link-border--inline"
            >
              {{ dateMonthFormat(row.vat_statement.from_date) }}
            </router-link>
          </div>
        </template>
        <template #verification_code="{ row }"><div class="table-default__left">{{ row.verification_code }}</div></template>
        <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
        <template #amount_total="{ row }">{{ formatAmount(row.amount_total) }}</template>
        <template #amount_excluded_vat="{ row }">{{ formatAmount(row.amount_excluded_vat) }}</template>
        <template #amount_vat="{ row }">{{ formatAmount(row.amount_vat) }}</template>
        <template #supplier="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
              class="table-default__link-inline"
            >
              {{ row.supplier.legal_name }}
            </router-link>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationRecords"
            v-if="pagination_records.pages > 1"
            v-model="pagination_records.page"
            :page-count="pagination_records.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_records.count" :count="table_records.items.length" :page="pagination_records.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import {
  getAdministrativeSuppliers, getFinanceSuppliersEFDReceipts,
  getFinanceVatStatementsRecordsList,
} from '@/http'
import debounce from 'lodash/debounce'
import {format, parseISO} from 'date-fns'
import AppColoredNotes from '@/components/AppColoredNotes.vue'

export default {
  name: 'FinanceEFDVATRecords',
  components: {AppColoredNotes, VueCtkDateTimePicker },
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      filter_dates: null,
      filter_supplier: null,
      repaint_key: 1,
      amount_total: '',
      custom_shortcuts: [
        { key: 'last7Days', label: 'Last 7 days', value: 7 },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'last60Days', label: 'Last 60 days', value: 60 },
        { key: 'last90Days', label: 'Last 90 days', value: 90 },
        { key: 'last180Days', label: 'Last 180 days', value: 180 },
        { key: 'thisYear', label: 'This year', value: 'year' },
        { key: 'lastYear', label: 'Last year', value: '-year' },
      ],
      supplier_options: [],
      business_unit_options: [],
      expense_item_options: [],
      responsible_options: [],
      currency_options: [],
      table_supplier: {
        items: [],
        columns: [
          'statement',
          'verification_code',
          'date',
          'supplier',
          'amount_total',
          'amount_excluded_vat',
          'amount_vat',
        ],
        options: {
          headings: {
            'statement': 'Statement',
            'verification_code': 'Verification Code',
            'date': 'Date',
            'amount_total': 'Total Amount',
            'amount_excluded_vat': 'VAT Exclude',
            'amount_vat': 'VAT',
            'supplier': 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_supplier: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_supplier: false,
      table_efd: {
        items: [],
        columns: [
          'statement',
          'verification_code',
          'date',
          'supplier',
          'amount_total',
          'amount_excluded_vat',
          'amount_vat',
        ],
        options: {
          headings: {
            'statement': 'Statement',
            'verification_code': 'Verification Code',
            'date': 'Date',
            'amount_total': 'Total Amount',
            'amount_excluded_vat': 'VAT Exclude',
            'amount_vat': 'VAT',
            'supplier': 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_efd: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_efd: false,
      table_purchases: {
        items: [],
        columns: [
          'verification_code',
          'date',
          'supplier',
          'amount_total',
          'amount_excluded_vat',
          'amount_vat',
        ],
        options: {
          headings: {
            'verification_code': 'Verification Code',
            'date': 'Date',
            'amount_total': 'Total Amount',
            'amount_excluded_vat': 'VAT Exclude',
            'amount_vat': 'VAT',
            'supplier': 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_purchases: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_purchases: false,
      table_amount: {
        items: [],
        columns: [
          'statement',
          'verification_code',
          'date',
          'supplier',
          'amount_total',
          'amount_excluded_vat',
          'amount_vat',
        ],
        options: {
          headings: {
            'statement': 'Statement',
            'verification_code': 'Verification Code',
            'date': 'Date',
            'amount_total': 'Total Amount',
            'amount_excluded_vat': 'VAT Exclude',
            'amount_vat': 'VAT',
            'supplier': 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_amount: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_amount: false,
      table_records: {
        items: [],
        columns: [
          'statement',
          'verification_code',
          'date',
          'supplier',
          'amount_total',
          'amount_excluded_vat',
          'amount_vat',
        ],
        options: {
          headings: {
            'statement': 'Statement',
            'verification_code': 'Verification Code',
            'date': 'Date',
            'amount_total': 'Total Amount',
            'amount_excluded_vat': 'VAT Exclude',
            'amount_vat': 'VAT',
            'supplier': 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_records: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_records: false,
    }
  },
  created() {
    Promise.all([
      this.fetchItemsSupplier(),
      this.fetchItemsEFD(),
      this.fetchItemsPurchases(),
      this.fetchItemsAmount(),
      this.fetchItemsRecords()
    ])
      .then(() => {
        this.page_loaded = true
      })
  },
  methods: {
    fetchAll() {
      this.fetchItemsSupplier()
      this.fetchItemsEFD()
      this.fetchItemsPurchases()
      this.fetchItemsAmount()
      this.fetchItemsRecords()
    },
    async fetchItemsSupplier() {
      const response = await getFinanceVatStatementsRecordsList({
        page: this.pagination_supplier.page,
        page_size: 20,
        search: this.filter_search,
        supplier: this.filter_search ? '' : this.filter_supplier?.id,
        category: 'missing_supplier',
        date__gte: this.filter_search ? '' : this.filter_dates?.start,
        date__lte: this.filter_search ? '' : this.filter_dates?.end,
      })
      this.table_supplier.items = response.data.results
      this.pagination_supplier.pages = response.data.pages
      this.pagination_supplier.count = response.data.count
      this.table_preloader_supplier = false
    },
    async fetchItemsEFD() {
      const response = await getFinanceVatStatementsRecordsList({
        page: this.pagination_efd.page,
        page_size: 20,
        search: this.filter_search,
        supplier: this.filter_search ? '' : this.filter_supplier?.id,
        category: 'missing_efd',
        date__gte: this.filter_search ? '' : this.filter_dates?.start,
        date__lte: this.filter_search ? '' : this.filter_dates?.end,
      })
      this.table_efd.items = response.data.results
      this.pagination_efd.pages = response.data.pages
      this.pagination_efd.count = response.data.count
      this.table_preloader_efd = false
    },
    async fetchItemsPurchases() {
      const response = await getFinanceSuppliersEFDReceipts({
        page: this.pagination_purchases.page,
        page_size: 20,
        search: this.filter_search,
        supplier: this.filter_search ? '' : this.filter_supplier?.id,
        statement_record: false,
        vat: true,
        from_date: this.filter_search ? '' : this.filter_dates?.start,
        to_date: this.filter_search ? '' : this.filter_dates?.end,
      })
      this.table_purchases.items = response.data.results
      this.pagination_purchases.pages = response.data.pages
      this.pagination_purchases.count = response.data.count
      this.table_preloader_purchases = false
    },
    async fetchItemsAmount() {
      const response = await getFinanceVatStatementsRecordsList({
        page: this.pagination_amount.page,
        page_size: 20,
        search: this.filter_search,
        supplier: this.filter_search ? '' : this.filter_supplier?.id,
        category: 'amount_not_match',
        date__gte: this.filter_search ? '' : this.filter_dates?.start,
        date__lte: this.filter_search ? '' : this.filter_dates?.end,
      })
      this.table_amount.items = response.data.results
      this.pagination_amount.pages = response.data.pages
      this.pagination_amount.count = response.data.count
      this.table_preloader_amount = false
    },
    async fetchItemsRecords() {
      const response = await getFinanceVatStatementsRecordsList({
        page: this.pagination_records.page,
        page_size: 20,
        search: this.filter_search,
        supplier: this.filter_search ? '' : this.filter_supplier?.id,
        date__gte: this.filter_search ? '' : this.filter_dates?.start,
        date__lte: this.filter_search ? '' : this.filter_dates?.end,
      })
      this.table_records.items = response.data.results
      this.pagination_records.pages = response.data.pages
      this.pagination_records.count = response.data.count
      this.table_preloader_records = false
    },
    onInputDatePicker() {
      if (!this.$refs.date_picker.pickerOpen) this.fetchAll()
    },
    onChangeFilter() {
      this.pagination_supplier.page = 1
      this.pagination_efd.page = 1
      this.pagination_purchases.page = 1
      this.pagination_amount.page = 1
      this.pagination_records.page = 1
      this.filter_search = ''
      this.fetchAll()
    },
    onClickPagination(page) {
      this.table_preloader_supplier = true
      this.pagination_supplier.page = page
      this.fetchItemsSupplier()
    },
    onClickPaginationEFD(page) {
      this.table_preloader_efd = true
      this.pagination_efd.page = page
      this.fetchItemsEFD()
    },
    onClickPaginationPurchases(page) {
      this.table_preloader_purchases = true
      this.pagination_purchases.page = page
      this.fetchItemsPurchases()
    },
    onClickPaginationAmount(page) {
      this.table_preloader_amount = true
      this.pagination_amount.page = page
      this.fetchItemsAmount()
    },
    onClickPaginationRecords(page) {
      this.table_preloader_records = true
      this.pagination_records.page = page
      this.fetchItemsRecords()
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    dateMonthFormat(date) {
      return date ? format(parseISO(date), 'MMM yyyy') : '-'
    },
    formatAmount(amount) {
      return new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(amount)
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search})
          .then(response => {
            vm.supplier_options = response.data
            loading(false)
          })
      }
    }, 500),
    onReceiptSearch() {
      this.searchReceipt(this)
    },
    searchReceipt: debounce(vm => {
      vm.pagination_supplier.page = 1
      vm.pagination_efd.page = 1
      vm.pagination_purchases.page = 1
      vm.pagination_amount.page = 1
      vm.pagination_records.page = 1
      vm.fetchAll()
    }, 350),
  }
}
</script>
